import { Track } from '../../types/Track';
import {
  getFileName,
  getFilenameScores,
  isExplicitVersion,
  isLiveVersion,
} from '../../utils/duplicateHelpers';
import { isDrmProtected } from './duplicateFiles';

const LOSSLESS_EXTENSIONS = new Set(['flac', 'wav', 'aiff', 'aif', 'alac']);

/** How far two copies' lengths can drift and still be the same recording. */
const SAME_LENGTH_TOLERANCE_S = 2;

/** Bitrate gap that is audible enough to decide on its own. */
const DECISIVE_BITRATE_GAP = 64;

/** Filename score gap that means one name is clearly the tidier one. */
const DECISIVE_NAME_GAP = 10;

const COPY_MARKERS = /copy|\(\d\)|duplicate|conflicted/i;

export type TrackFacts = {
  track: Track;
  fileName: string;
  extension: string;
  bitrate?: number;
  duration?: number;
  isLossless: boolean;
  isDrm: boolean;
  cues: number;
  loops: number;
  hasBeatgrid: boolean;
  playCount: number;
  rating?: number;
  nameScore: number;
  looksLikeACopy: boolean;
  /** Cue points, loops and beatgrids the DJ set by hand. */
  djWork: number;
  /** Serato crates that reference this file path. */
  crateNames: string[];
  crateCount: number;
};

/** Why a group can't be decided automatically, in words a user can act on. */
export type OpenQuestion = {
  kind:
    | 'length'
    | 'shared-work'
    | 'version'
    | 'edit'
    | 'drm'
    | 'toss-up'
    | 'crates-vs-work';
  text: string;
};

export type Verdict = {
  keeperId: number;
  removeIds: number[];
  /** `clear` groups can be applied in bulk; `review` groups need the user. */
  confidence: 'clear' | 'review';
  /** One sentence: why this copy wins. */
  headline: string;
  questions: OpenQuestion[];
  facts: TrackFacts[];
};

const countList = (value: unknown[] | undefined): number =>
  Array.isArray(value) ? value.length : 0;

export const formatSeconds = (seconds?: number): string | undefined => {
  if (
    typeof seconds !== 'number' ||
    !Number.isFinite(seconds) ||
    seconds <= 0
  ) {
    return undefined;
  }
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
};

export const readTrackFacts = (
  tracks: Track[],
  cratesForTrack?: (track: Track) => string[],
): TrackFacts[] => {
  const nameScores = getFilenameScores(tracks);

  return tracks.map((track, index) => {
    const fileName = getFileName(track.filePath);
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    const cues = countList(track.tags?.serato_cues);
    const loops = countList(track.tags?.serato_loops);
    const hasBeatgrid = countList(track.tags?.serato_beatgrid) > 0;
    const crateNames = cratesForTrack?.(track) ?? [];

    return {
      track,
      fileName,
      extension,
      bitrate: track.tags?.bitrate,
      duration: track.tags?.duration,
      isLossless: LOSSLESS_EXTENSIONS.has(extension),
      isDrm: isDrmProtected(track.filePath),
      cues,
      loops,
      hasBeatgrid,
      playCount: track.seratoPlayCount ?? track.tags?.serato_played_count ?? 0,
      rating: track.tags?.serato_rating,
      nameScore: nameScores.find((s) => s.index === index)?.score ?? 0,
      looksLikeACopy: COPY_MARKERS.test(fileName),
      djWork: cues + loops + (hasBeatgrid ? 1 : 0),
      crateNames,
      crateCount: crateNames.length,
    };
  });
};

const describeDjWork = (facts: TrackFacts): string => {
  const parts: string[] = [];
  if (facts.cues > 0) {
    parts.push(`${facts.cues} cue point${facts.cues === 1 ? '' : 's'}`);
  }
  if (facts.loops > 0) {
    parts.push(`${facts.loops} loop${facts.loops === 1 ? '' : 's'}`);
  }
  if (parts.length === 0 && facts.hasBeatgrid) parts.push('a set beatgrid');
  return parts.join(' and ');
};

/**
 * Ranks copies the way a DJ would: the copy you have already put work into
 * beats a technically better file you have never touched.
 */
const compareCandidates = (a: TrackFacts, b: TrackFacts): number => {
  if (a.isDrm !== b.isDrm) return a.isDrm ? 1 : -1;
  if (a.crateCount !== b.crateCount) return b.crateCount - a.crateCount;
  if (a.djWork !== b.djWork) return b.djWork - a.djWork;
  if (a.playCount !== b.playCount) return b.playCount - a.playCount;
  if (a.isLossless !== b.isLossless) return a.isLossless ? -1 : 1;
  if ((a.bitrate ?? 0) !== (b.bitrate ?? 0)) {
    return (b.bitrate ?? 0) - (a.bitrate ?? 0);
  }
  return b.nameScore - a.nameScore;
};

/** The single most defensible reason the winner beat the runner-up. */
const explainWinner = (winner: TrackFacts, others: TrackFacts[]): string => {
  const work = describeDjWork(winner);

  if (
    winner.crateCount > 0 &&
    others.every((other) => other.crateCount === 0)
  ) {
    const sample = winner.crateNames.slice(0, 3).join(', ');
    const more =
      winner.crateCount > 3 ? ` (+${winner.crateCount - 3} more)` : '';
    return `This copy is in ${winner.crateCount} Serato crate${
      winner.crateCount === 1 ? '' : 's'
    } (${sample}${more}). The others aren't in any crates.`;
  }

  if (winner.djWork > 0 && others.every((other) => other.djWork === 0)) {
    return work
      ? `This is the copy you've worked on — it has ${work}.`
      : 'This is the copy your Serato data is attached to.';
  }

  if (winner.playCount > 0 && others.every((o) => o.playCount === 0)) {
    return `You've played this copy ${winner.playCount} time${
      winner.playCount === 1 ? '' : 's'
    }. The others you've never played.`;
  }

  if (winner.isLossless && others.every((other) => !other.isLossless)) {
    return `This is the only lossless copy (${winner.extension.toUpperCase()}).`;
  }

  const bestOther = Math.max(...others.map((other) => other.bitrate ?? 0));
  if (winner.bitrate && bestOther && winner.bitrate > bestOther) {
    return `This is the highest quality copy — ${winner.bitrate} kbps against ${bestOther} kbps.`;
  }

  if (others.every((other) => other.looksLikeACopy) && !winner.looksLikeACopy) {
    return 'This one has the original filename — the others are named like copies.';
  }

  if (winner.nameScore > Math.max(...others.map((other) => other.nameScore))) {
    return 'This one has the cleanest filename and tags.';
  }

  return 'These copies look equivalent, so this keeps the first one found.';
};

const findOpenQuestions = (facts: TrackFacts[]): OpenQuestion[] => {
  const questions: OpenQuestion[] = [];

  const durations = facts
    .map((f) => f.duration)
    .filter((d): d is number => typeof d === 'number' && d > 0);
  if (durations.length > 1) {
    const shortest = Math.min(...durations);
    const longest = Math.max(...durations);
    if (longest - shortest > SAME_LENGTH_TOLERANCE_S) {
      questions.push({
        kind: 'length',
        text: `These aren't the same length (${formatSeconds(shortest)} vs ${formatSeconds(
          longest,
        )}), so they may be different edits rather than duplicates.`,
      });
    }
  }

  const withWork = facts.filter((f) => f.djWork > 0);
  if (withWork.length > 1) {
    questions.push({
      kind: 'shared-work',
      text: `You've set cues or loops on ${withWork.length} of these copies. Removing one loses that work.`,
    });
  }

  const liveFlags = new Set(
    facts.map(
      (f) =>
        isLiveVersion(f.fileName) || isLiveVersion(f.track.tags?.title ?? ''),
    ),
  );
  if (liveFlags.size > 1) {
    questions.push({
      kind: 'version',
      text: 'One of these looks like a live or acoustic version, which is a different recording.',
    });
  }

  const explicitFlags = new Set(
    facts.map((f) => isExplicitVersion(f.fileName)),
  );
  if (explicitFlags.size > 1) {
    questions.push({
      kind: 'edit',
      text: 'One of these looks like a clean or explicit edit of the other.',
    });
  }

  if (facts.some((f) => f.isDrm)) {
    questions.push({
      kind: 'drm',
      text: 'One of these is DRM-protected, so it may not play outside iTunes.',
    });
  }

  const crateLeader = [...facts].sort((a, b) => b.crateCount - a.crateCount)[0];
  const workLeader = [...facts].sort((a, b) => b.djWork - a.djWork)[0];
  if (
    crateLeader.crateCount > 0 &&
    workLeader.djWork > 0 &&
    crateLeader.track.id !== workLeader.track.id
  ) {
    const crateSample = crateLeader.crateNames.slice(0, 2).join(', ');
    const work = describeDjWork(workLeader);
    questions.push({
      kind: 'crates-vs-work',
      text: `One copy is in ${crateLeader.crateCount} crate${
        crateLeader.crateCount === 1 ? '' : 's'
      } (${crateSample}) but another has ${work}. Keeping the wrong one breaks crates or loses your work.`,
    });
  }

  return questions;
};

/** True when the winner beat the runner-up on something a user can hear or see. */
const isDecisive = (winner: TrackFacts, runnerUp: TrackFacts): boolean => {
  if (winner.isDrm !== runnerUp.isDrm) return true;
  if (winner.crateCount >= 1 && runnerUp.crateCount === 0) return true;
  if (winner.djWork > 0 && runnerUp.djWork === 0) return true;
  if (winner.isLossless && !runnerUp.isLossless) return true;
  if ((winner.bitrate ?? 0) - (runnerUp.bitrate ?? 0) >= DECISIVE_BITRATE_GAP) {
    return true;
  }
  if (runnerUp.looksLikeACopy && !winner.looksLikeACopy) return true;
  return winner.nameScore - runnerUp.nameScore >= DECISIVE_NAME_GAP;
};

/**
 * Decides what to keep in one duplicate group and how sure that decision is.
 * Groups that come back `clear` are safe to apply without the user reading
 * them; `review` groups get put in front of the user one at a time.
 */
export const judgeGroup = (
  tracks: Track[],
  cratesForTrack?: (track: Track) => string[],
): Verdict | null => {
  if (!tracks || tracks.length < 2) return null;

  const facts = readTrackFacts(tracks, cratesForTrack);
  const ranked = [...facts].sort(compareCandidates);
  const [winner, runnerUp] = ranked;
  const others = ranked.slice(1);

  const questions = findOpenQuestions(facts);
  const decisive = isDecisive(winner, runnerUp);

  if (!decisive && questions.length === 0) {
    questions.push({
      kind: 'toss-up',
      text: 'These copies look equivalent, so there is no clearly better one to keep.',
    });
  }

  return {
    keeperId: winner.track.id,
    removeIds: others.map((other) => other.track.id),
    confidence: questions.length === 0 && decisive ? 'clear' : 'review',
    headline: explainWinner(winner, others),
    questions,
    facts: ranked,
  };
};
